import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/Card';
import { Loader2, Download } from 'lucide-react';
import {
  fetchClosedReferralFilterOptions,
  fetchClosedReferrals,
} from '../../services/referralService';
import { useWorkflowBar } from '../../contexts/WorkflowBarContext';
import { ReferralTable, DEFAULT_SORT, REFERRAL_COLUMNS, getReferralSelectionId, getVisibleReferralColumns } from '../../components/referrals/ReferralTable';
import { ReferralDrawer } from '../../components/referral-drawer/ReferralDrawer';
import { useReferralList } from '../../hooks/useReferralList';
import { usePageTitle } from '../../hooks/usePageTitle';
import { getCurrentUser, getOrgType, isSmartLightEmployee } from '../../authUtils';


function ClosedReferralListPage() {
  usePageTitle('Referral Portal - Closed Referrals');
  const { setVisible } = useWorkflowBar();
  const { searchText = '' } = useOutletContext() || {};

  const {
    referrals,
    totalCount,
    loading,
    error,
    sortColumns,
    isDefaultSort,
    openSortMenu,
    setOpenSortMenu,
    filters,
    setFilters,
    filterOptions,
    handleSort,
    handleClearColumnSort,
  } = useReferralList(fetchClosedReferrals, searchText, {
    applyDefaultSort: false,
    fetchOptionsFn: fetchClosedReferralFilterOptions,
  });

  // Drawer state — closed referrals open in read-only mode
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const handleRowDoubleClick = (referral) => {
    setSelectedReferral(referral);
    setDrawerOpen(true);
  };

  const visibleColumns = getVisibleReferralColumns(REFERRAL_COLUMNS, {
    isInternal: isSmartLightEmployee(),
    orgType: getOrgType(),
    displayRules: getCurrentUser()?.display_rules,
  });

  // Workflow bar is not relevant on the closed referrals page — keep it hidden
  useEffect(() => {
    setVisible(false);
    return () => setVisible(false);
  }, [setVisible]);

  useEffect(() => {
    if (loading || selectedRowId === null) return;
    const selectedReferralAvailable = referrals.some((referral) => getReferralSelectionId(referral) === selectedRowId);
    if (!selectedReferralAvailable) {
      setSelectedRowId(null);
    }
  }, [loading, referrals, selectedRowId]);

  return (
    <div className="bg-background font-sans antialiased -m-6 h-[calc(100vh-88px)] flex flex-col">
      {/* Referral detail drawer (read-only) */}
      <ReferralDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        referral={selectedReferral}
        readOnly
      />
      <div className="w-full px-4 sm:px-6 lg:px-8 pt-0 pb-6 flex flex-col flex-1 min-h-0">
        <Card className="flex flex-col flex-1 min-h-0">
          <CardContent className="p-0 flex flex-col flex-1 min-h-0">
            {loading ? (
              <div className="flex items-center justify-center py-12" aria-label="Loading">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-destructive">Error: {error}</p>
              </div>
            ) : (
              <div className="overflow-x-auto flex-1 min-h-0">
                <ReferralTable
                  columns={visibleColumns}
                  rows={referrals}
                  emptyStateText="No referrals found."
                  sortColumns={sortColumns}
                  isDefaultSort={isDefaultSort}
                  defaultSort={DEFAULT_SORT}
                  openSortMenu={openSortMenu}
                  setOpenSortMenu={setOpenSortMenu}
                  onSort={handleSort}
                  onClearColumnSort={handleClearColumnSort}
                  filters={filters}
                  filterOptions={filterOptions}
                  onFilterChange={(filterKey, val) => setFilters(prev => ({ ...prev, [filterKey]: val }))}
                  onRowDoubleClick={handleRowDoubleClick}
                  selectedRowId={selectedRowId}
                  onSelectedRowChange={setSelectedRowId}
                />
                {/* No stepLabels / onWorkflowCounts on the closed page — the */}
                {/* workflow bar is not relevant here. Default Table Sort */}
                {/* button is also omitted (no setSortColumns/setIsDefaultSort). */}
              </div>
            )}
            {!loading && !error && referrals.length > 0 && (
              <div className="sticky bottom-0 z-10 flex items-center justify-center gap-4 border-t border-border bg-accent px-3 py-1">
                <span className="text-xs text-muted-foreground">
                  {referrals.length} of {totalCount} results
                </span>
                {/* TODO: wire up download functionality */}
                <button
                  type="button"
                  className="h-6 px-4 text-xs font-semibold flex items-center gap-1.5 text-muted-foreground hover:text-foreground bg-background hover:bg-accent border border-muted-foreground/30 hover:border-foreground rounded transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Download
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export { ClosedReferralListPage };
